import isempty from 'lodash/isEmpty';

// Relative Imports
import {pricingGraphql} from '../api/pricingGraphQl';
import {log as logToServer} from '../api/logger';
import edapActions from '../actions/edapActions';
import listingActions from '../actions/listingActions';
import {priceErrorMap, types} from './errorEvents';
import {Map} from 'immutable';
import {reportError, reportSuccess} from '@homeaway/react-error-reporter';

function createDefaultPricingKey(tripData, systemData) {
    return new Map({
        arrival: tripData.arriveDateText,
        departure: tripData.departDateText,
        adults: tripData.adults,
        children: tripData.children,
        pets: tripData.pets,
        currency: systemData.currency
    });
}

function getError(checkoutError) {
    const errorCode = checkoutError ? checkoutError.errorCode : 'UNDEFINED';

    if (errorCode) {
        return priceErrorMap[errorCode] || priceErrorMap.UNDEFINED;
    }

    return null;
}

/* eslint-disable complexity */
function handleError(store, pageLoadPriceCall = false, checkoutError, quoteEventParams) {
    const error = getError(checkoutError);

    if (error) {
        const validDates = error.type !== types.DATES;
        const validGuests = error.type !== types.GUESTS;

        store.dispatch(
            listingActions.datesAreValid({
                datesValid: validDates
            })
        );

        store.dispatch(listingActions.guestsAreValid(validGuests));

        const {listingReducer, tripDetailsReducer, systemReducer} = store.getState();
        if (!validDates || !validGuests) {
            const defaultPricingKey = createDefaultPricingKey(tripDetailsReducer, systemReducer);
            store.dispatch(listingActions.pricingInfoReset(defaultPricingKey));
        }

        store.dispatch(edapActions.triggerEdapEvent('quote.failure', quoteEventParams));
        reportError({name: 'quote failure'});

        const graphqlRequestMarker =
            checkoutError && checkoutError.graphqlRequestMarker
                ? checkoutError.graphqlRequestMarker
                : null;

        logToServer({
            message: 'quote.attempt',
            quoteSuccess: false,
            pageLoadPriceCall: pageLoadPriceCall.toString(),
            errorCode: checkoutError ? checkoutError.errorCode : 'UNDEFINED',
            ownerManaged: listingReducer.ownerManaged,
            integratedPropertyManager: listingReducer.integratedPropertyManager,
            graphqlRequestMarker,
            ...quoteEventParams
        });
    }
}
/* eslint-enable complexity */

/* eslint-disable complexity */
function handleGraphQlResult(
    store,
    pageLoadPriceCall,
    priceDetails,
    arrival,
    departure,
    adults,
    children,
    currency,
    pets,
    clientPriceFetchTime,
    displayLocale,
    defaultadults
) {
    const {listingReducer} = store.getState();

    const priceDetailsData = priceDetails.priceDetails || {};
    const priceDetailsError = priceDetails.originalErrorCode;
    const quoteEventParams = priceDetailsData.edapQuoteSuccess
        ? JSON.parse(priceDetailsData.edapQuoteSuccess)
        : {};
    quoteEventParams.defaultadults = defaultadults;
    if (priceDetailsError || isempty(priceDetailsData)) {
        const checkoutError = {
            errorCode: priceDetailsError || 'API_BROKEN'
        };
        if (priceDetails.graphqlRequestMarker) {
            checkoutError.graphqlRequestMarker = priceDetails.graphqlRequestMarker;
        }

        const quoteFailureEventParams = {
            eventstartdate: arrival,
            eventenddate: departure,
            eventnumadults: `${adults}`,
            eventnumchildren: `${children}`,
            eventrequestmarker: priceDetails.graphqlRequestMarker,
            eventerrorcode: checkoutError.errorCode.toString()
        };
        handleError(store, pageLoadPriceCall, checkoutError, quoteFailureEventParams);
    } else {
        store.dispatch(edapActions.triggerEdapEvent('quote.success', quoteEventParams));
        reportSuccess({name: 'quote success'});
        logToServer({
            message: 'quote.attempt',
            pageLoadPriceCall: pageLoadPriceCall.toString(),
            clientPriceFetchTime,
            quoteSuccess: true,
            eventHasPets: pets,
            ...quoteEventParams,
            ownerManaged: listingReducer.ownerManaged,
            integratedPropertyManager: listingReducer.integratedPropertyManager,
            displayLocale,
            graphqlRequestMarker: priceDetails.graphqlRequestMarker
        });
    }
}
/* eslint-enable complexity */

/* eslint-disable complexity */
const pricingHelper = async ({
    store,
    listingId = '',
    displayLocale = '',
    arrival = '',
    departure = '',
    adults = 0,
    children = 0,
    pets = 0,
    defaultadults = 'false',
    currency = '',
    loadingRate = () => {},
    loadedRate = () => {},
    pageLoadPriceCall = false,
    unitApiUrl = ''
}) => {
    store.dispatch(loadingRate(true));
    store.dispatch(
        edapActions.triggerEdapEvent('quote.attempt', {
            eventstartdate: arrival,
            eventenddate: departure,
            eventnumadults: `${adults}`,
            eventnumchildren: `${children}`,
            defaultadults
        })
    );

    const startPricingCall = Date.now();

    try {
        const graphqlPricingResult = await pricingGraphql(
            {
                unit: unitApiUrl,
                arrivalDate: arrival,
                departureDate: departure,
                adults,
                children,
                pets,
                listingRef: listingId,
                currency
            },
            displayLocale,
            store
        );

        const pricingKey = {
            arrival,
            departure,
            adults,
            children,
            currency,
            pets
        };
        let clientPriceFetchTime = graphqlPricingResult.endTime - startPricingCall;

        if (graphqlPricingResult.errorCode && !graphqlPricingResult.originalErrorCode) {
            graphqlPricingResult.originalErrorCode = graphqlPricingResult.errorCode;
        }

        handleGraphQlResult(
            store,
            pageLoadPriceCall,
            graphqlPricingResult,
            arrival,
            departure,
            adults,
            children,
            currency,
            pets,
            clientPriceFetchTime,
            displayLocale,
            defaultadults
        );

        store.dispatch(loadedRate({}, graphqlPricingResult, pricingKey));
    } catch (err) {
        // The only exception that should be caught here is for the primary pricing call.
        // The secondary compared call was wrapped in the 'whoCaresIfFailedPromise' Promise.
        const checkoutError = {
            id: 'UNKNOWN',
            errorCode: 'INTERNAL_ERROR',
            message: `Pricing call error: ${err}`
        };

        handleError(store, pageLoadPriceCall, checkoutError, {});

        // If graphql was primary, fill in the empty structure's error code.
        store.dispatch(loadedRate({[listingId]: {checkoutError}}, {}, checkoutError));
        store.dispatch(loadingRate(false));
    }
};
/* eslint-enable complexity */

export default pricingHelper;
export {getError, handleError, handleGraphQlResult, pricingHelper, createDefaultPricingKey};
